import { criterion, fail, fromCriteria } from "@/core/check";
import type { CheckResult, Lesson } from "@/core/types";
import {
  hasUniformFormatting,
  paragraphsWithStyle,
  readDocument,
  type ParagraphInfo,
} from "@/office/word/document";
import { findStyle, readStyles } from "@/office/word/styles";

/**
 * Prüft, ob eine Formatvorlage auf eine bestimmte Schriftgröße geändert wurde.
 *
 * Bevorzugt wird die Definition der Vorlage selbst ausgelesen – nur so lässt
 * sich eine echte Vorlagenänderung von einer manuellen Formatierung
 * unterscheiden. Steht die dafür nötige API nicht zur Verfügung, wird ersatzweise
 * geprüft, ob alle betroffenen Absätze einheitlich in der gewünschten Größe
 * stehen.
 */
async function pruefeVorlagengroesse(
  styleBuiltIn: string,
  anzeigename: string,
  groesse: number,
  texte: { success: string; failure: string; hint: string },
): Promise<CheckResult> {
  const snapshot = await readDocument();
  const absaetze: ParagraphInfo[] = paragraphsWithStyle(snapshot, styleBuiltIn);

  if (absaetze.length === 0) {
    return fail(
      `Es gibt noch keinen Absatz mit der Vorlage „${anzeigename}“.`,
      "Weise die Vorlage zuerst einem Absatz zu – sonst lässt sich die Änderung nicht prüfen.",
    );
  }

  const styles = await readStyles();
  const definition = findStyle(styles, absaetze[0].styleName);

  if (definition) {
    return fromCriteria(
      [
        criterion(
          `Die Vorlage „${definition.nameLocal}“ steht auf ${groesse} pt (aktuell: ${
            definition.font.size ?? "unbekannt"
          } pt)`,
          definition.font.size === groesse,
        ),
        criterion(
          "Alle Absätze mit dieser Vorlage sind einheitlich formatiert",
          hasUniformFormatting(absaetze),
        ),
      ],
      texte,
    );
  }

  // Ersatzweg für Word-Versionen ohne Zugriff auf die Vorlagendefinition.
  const alleInGroesse = absaetze.every((absatz) =>
    absatz.runs.every((run) => run.size === groesse),
  );
  return fromCriteria(
    [
      criterion(`Alle Absätze der Vorlage stehen auf ${groesse} pt`, alleInGroesse),
      criterion("Sie sind einheitlich formatiert", hasUniformFormatting(absaetze)),
    ],
    texte,
  );
}

export const lektionFormatvorlagenAnpassen: Lesson = {
  id: "anpassen",
  title: "Formatvorlagen ändern statt Text umformatieren",
  summary: "Einmal die Vorlage anpassen – und das ganze Dokument zieht mit.",
  durationMinutes: 10,
  learningGoals: [
    "Eine Formatvorlage über den Ändern-Dialog anpassen",
    "Eine Vorlage aus einer formatierten Auswahl aktualisieren",
    "Direkte Formatierung als Störfaktor erkennen",
  ],
  steps: [
    {
      id: "aendern-dialog",
      title: "Eine Vorlage über den Ändern-Dialog anpassen",
      explanation: [
        "Jetzt kommt der eigentliche Gewinn. Gefällt dir das Aussehen einer Überschrift " +
          "nicht, formatierst du nicht jede einzelne Überschrift neu – du änderst die " +
          "Vorlage. Word passt daraufhin jeden Absatz an, der diese Vorlage verwendet, " +
          "auch in einem Dokument mit hundert Kapiteln.",
        "Klicke dafür im Katalog „Formatvorlagen“ mit der rechten Maustaste auf " +
          "„Überschrift 1“ und wähle „Ändern…“. Im Dialog stellst du Schriftart, Größe " +
          "und Farbe ein und bestätigst mit OK.",
        "Wichtig ist die Auswahl unten im Dialog: „Nur in diesem Dokument“ ändert die " +
          "Vorlage nur hier, „Neue auf dieser Vorlage basierende Dokumente“ übernimmt " +
          "die Änderung dauerhaft in die zugrunde liegende Dokumentvorlage.",
      ],
      keyPoints: [
        "Rechtsklick auf die Vorlage → „Ändern…“.",
        "Eine Änderung wirkt sofort auf alle Absätze mit dieser Vorlage.",
        "Im Zweifel „Nur in diesem Dokument“ wählen.",
      ],
      task: {
        instruction:
          "Ändere die Formatvorlage „Überschrift 1“ so, dass sie die Schriftgröße 20 pt hat.",
        hint: "Rechtsklick auf „Überschrift 1“ im Katalog → „Ändern…“ → im Feld für die Schriftgröße 20 eintragen → OK.",
        check: () =>
          pruefeVorlagengroesse("Heading1", "Überschrift 1", 20, {
            success: "Die Vorlage ist angepasst – und mit ihr jede Überschrift der Ebene 1.",
            failure: "Die Vorlage steht noch nicht auf 20 pt.",
            hint: "Hast du die Schriftgröße nur bei der markierten Überschrift geändert? Dann bleibt die Vorlage unverändert. Nutze den Rechtsklick auf die Vorlage und „Ändern…“.",
          }),
      },
    },
    {
      id: "aus-auswahl-aktualisieren",
      title: "Eine Vorlage aus der Auswahl aktualisieren",
      explanation: [
        "Es gibt einen zweiten, oft schnelleren Weg: Formatiere einen einzelnen Absatz " +
          "so, wie er aussehen soll. Klicke dann im Katalog mit der rechten Maustaste auf " +
          "seine Vorlage und wähle „<Name> aktualisieren, um der Auswahl anzupassen“.",
        "Word übernimmt die Formatierung des Absatzes in die Vorlage – und damit in alle " +
          "anderen Absätze mit derselben Vorlage. Praktisch, wenn man beim Ausprobieren " +
          "schon das richtige Aussehen gefunden hat.",
        "Denk daran, dass „Standard“ die Grundlage der meisten anderen Vorlagen ist. " +
          "Eine Änderung dort wirkt sich auch auf die Vorlagen aus, die darauf aufbauen.",
      ],
      keyPoints: [
        "Absatz formatieren → Rechtsklick auf die Vorlage → „… aktualisieren, um der Auswahl anzupassen“.",
        "„Standard“ ist die Basisvorlage – Änderungen dort haben die größte Reichweite.",
      ],
      task: {
        instruction:
          "Stelle die Vorlage „Standard“ auf 12 pt um – am besten über den Weg " +
          "„aktualisieren, um der Auswahl anzupassen“.",
        hint: "Setze einen Fließtextabsatz auf 12 pt, dann Rechtsklick auf „Standard“ im Katalog und aktualisieren.",
        check: () =>
          pruefeVorlagengroesse("Normal", "Standard", 12, {
            success: "Sehr gut – dein gesamter Fließtext steht jetzt einheitlich auf 12 pt.",
            failure: "Die Vorlage „Standard“ steht noch nicht durchgängig auf 12 pt.",
            hint: "Prüfe, ob wirklich alle Fließtextabsätze mitgezogen sind. Bleibt einer zurück, wurde er vermutlich von Hand formatiert.",
          }),
      },
    },
    {
      id: "direkte-formatierung",
      title: "Direkte Formatierung sticht die Vorlage",
      explanation: [
        "Eine Sache musst du kennen, sonst wirkt Word später unberechenbar: Direkte " +
          "Formatierung hat immer Vorrang vor der Vorlage. Hast du eine Überschrift von " +
          "Hand auf 14 pt gesetzt, bleibt sie bei 14 pt – auch wenn du die Vorlage später " +
          "auf 20 pt änderst.",
        "Solche Absätze fallen erst auf, wenn ein Dokument wächst. Deshalb lohnt es sich, " +
          "beim Übernehmen fremder Texte zuerst aufzuräumen: Text markieren und mit " +
          "Strg + Leertaste die Zeichenformatierung entfernen oder auf der Registerkarte " +
          "„Start“ die Schaltfläche „Alle Formatierungen löschen“ verwenden.",
        "Als Faustregel: Sobald du dieselbe Formatierung zum zweiten Mal von Hand " +
          "vergibst, gehört sie in eine Formatvorlage.",
      ],
      keyPoints: [
        "Direkte Formatierung überschreibt die Vorlage – dauerhaft.",
        "Strg + Leertaste entfernt die Zeichenformatierung der Markierung.",
        "Strg + Umschalt + N setzt einen Absatz auf „Standard“ zurück.",
      ],
    },
  ],
};
